/**
 * Grupos de sistema (T50, PAPEL-08, PADRONIZACAO-ROLES.md §4.4 e §4.5).
 *
 * Um grupo é um **nome para uma lista de papéis**, nada mais. Não tem concessão
 * própria, não tem condição, não tem precedência: quem pertence ao grupo tem os
 * papéis que ele lista, e é só isso. Essa pobreza é o ponto — é o que faz "o que
 * um site manager pode fazer" caber numa linha legível em vez de estar espalhado
 * por sete arquivos de política.
 *
 * As seis strings abaixo estão hoje em `roles[]` como se fossem papéis, e não são:
 * `catalogo/legado.ts` mapeia todas para `null` justamente por isso. `site_manager`
 * aparece em oito arquivos de política; `pcp` e `planejamento` são etapas de
 * workflow; `mkt_read` concede leitura ampla. Nenhuma delas responde "quanto acesso"
 * num domínio, que é o que um papel responde.
 *
 * **Isto é declaração, não ligação.** Os arquivos de política continuam lendo
 * `temPermissao(["site_manager"])` até a Fase 7 (T51 a T54) construir os
 * `PAPEIS_<Dominio>` e passar a compor por papel. O valor de existir agora é
 * duplo: torna o conteúdo de cada grupo revisável por gente, e dá ao teste de
 * invariante (T45) um alvo real — grupo fora de CAIXA_ALTA ou papel fora da
 * gramática reprovam a partir daqui.
 */
import { DOMINIOS, type Dominio, type NomePapel } from "./vocabulario";

/**
 * Domínios que o grupo MKT NÃO lê.
 *
 * `acesso` é o domínio meta, que administra o próprio catálogo de permissões —
 * marketing não administra permissão de ninguém.
 */
const FORA_DO_MKT: readonly Dominio[] = ["acesso"];

/**
 * Leitura de todos os domínios de negócio, um papel por domínio.
 *
 * Derivado de `DOMINIOS` em vez de escrito à mão: domínio novo entra no grupo
 * sozinho, e não fica de fora porque alguém esqueceu de atualizar duas listas.
 */
const LEITURA_DE_TODOS_OS_DOMINIOS: readonly NomePapel[] = DOMINIOS.filter(
  (dominio) => !FORA_DO_MKT.includes(dominio),
).map((dominio) => `${dominio}.leitura` as NomePapel);

export const GRUPOS = {
  /**
   * Site manager de obra. Os papéis vêm do que os oito arquivos de política hoje
   * concedem sob `temPermissao(["site_manager"])`, consolidados
   * (`ANALISE-CONTROLE-ACESSO.md` §4.2).
   *
   * O que o grupo NÃO carrega, de propósito: as condições. Hoje quase toda regra
   * de site manager é condicional a `"<campo>.mail" === user.email` — ser site
   * manager de UMA obra, não de todas. Essa parte é relação na entidade, não
   * papel, e é o que o `RELACOES` da Fase 7 (T51) resolve. Um grupo que
   * carregasse a condição seria a terceira cópia do mesmo fato (§8, decisão 1).
   *
   * **Três papéis entraram na T56 (AD-031 D5 e D6).** `contratos.escrita_propria`
   * e `materiais.escrita_propria` dão dono às duas concessões que a T52 tinha
   * deixado soltas — a edição de contrato em "Aprovado" e a liberação de material
   * em "LIBERADO PARA SITE MANAGER", as duas por relação. E `rdo.leitura` entra
   * porque o usuário decidiu, textualmente, que o site manager **lê RDO de todas
   * as obras e cria só na sua**: a criação fica em `rdo.escrita`, sob relação, e a
   * leitura ampla é papel próprio.
   *
   * **Mais dois na T128 (AD-031 D3):** `automacao.criacao` e `field.criacao`. Eram
   * as concessões que `Automacao.ts:35` e `FieldServices.ts:41` davam a
   * `site_manager` **por fora de qualquer papel** — a divergência 1 do handoff.
   * Só o `create`: a metade "ler as OS da sua obra" é escopo de usuário e está
   * aberta como decisão de negócio na T128.
   */
  SITE_MANAGER: [
    "materiais.leitura",
    "materiais.escrita_propria",
    "projetos.leitura",
    "projetos.upload",
    "projetos_fabrica.leitura",
    "projetos_fabrica.upload",
    "contratos.leitura",
    "contratos.escrita_propria",
    "seguros.leitura",
    "sgi.leitura",
    "field.apoio_os",
    "field.criacao",
    "automacao.escrita",
    "automacao.criacao",
    "rdo.escrita",
    "rdo.leitura",
  ],

  /** Etapa de workflow do RDO: configura e lê (`rdo.ts:24`). */
  PLANEJAMENTO: ["rdo.config", "rdo.leitura"],

  /** Etapa de workflow de materiais: lê e libera no status certo (`materiais.ts:65-72`). */
  PCP: ["materiais.leitura", "materiais.liberacao"],

  /**
   * Marketing.
   *
   * **Muda comportamento, e é visível.** Hoje `mkt_read` concede
   * `can("read", "all")` — leitura de tudo, inclusive de dado de cliente e dos
   * módulos de relação (Jurídico, Interações SGI). Como grupo de `*.leitura`, a
   * leitura passa a respeitar `VISIBILIDADE` e escopo, e o marketing pode perder
   * acesso a esses módulos. É a mudança correta (§7d), mas precisa de aviso à área
   * antes do merge — e é por isso que este grupo está declarado aqui sem ainda
   * substituir o `can("read", "all")` de `shared/autorizacao/index.ts`.
   */
  MKT: LEITURA_DE_TODOS_OS_DOMINIOS,

  /**
   * Cliente que só lê o que lhe é aberto.
   *
   * **`projetos.leitura_externa`, e não `projetos.leitura` (T56, AD-031 D4a).** A
   * leitura interna dá `readInterno` em `PROJETO`, que é dado do projetista Gali;
   * carregá-la aqui entregava esse dado a usuário externo. Fecha a divergência 2
   * do handoff.
   */
  CLIENTE_LEITOR: ["projetos.leitura_externa", "obra.leitura"],

  /** Cliente que também aprova projeto — hoje só no estado `PAP` (AD-031 D4b). */
  CLIENTE_APROVADOR: ["projetos.leitura_externa", "obra.leitura", "projetos.aprovacao"],
} as const satisfies Record<string, readonly NomePapel[]>;

/** Nome de um grupo de sistema. */
export type NomeGrupo = keyof typeof GRUPOS;
